import React, { useCallback, useEffect, useState } from "react";
import {
  AlertCircle,
  CheckCheck,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  Edit,
  ExternalLink,
  Loader2,
  MessageSquare,
  Plus,
  RefreshCw,
  Trash2,
} from "lucide-react";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";

import PageHeader from "../../../components/shared/PageHeader";
import ReportIssueModal from "../components/ReportIssueModal";
import EditIssueModal from "../components/EditIssueModal";
import {
  deleteErrorReport,
  getErrorReports,
  retryErrorReport,
} from "../api/errorReportsApi";
import { hasAdminAccess } from "../../../utils/roleAccess";
import { formatMelbourneDateTime } from "../../../utils/melbourneTime";

const BRAND = "#55C5D0";
const PAGE_SIZE = 20;

// cg@ can delete ANY report (the original single cross-report deleter). A
// reporter can also delete their OWN report (see isOwnReport gate on the delete
// button, mirrored by the backend). Delete also archives the Monday item.
const DELETER_EMAIL = "cg@collings.com.au";

const STATUS_STYLES = {
  synced: { label: "Sent to Monday", cls: "bg-green-50 text-green-700 border-green-200", Icon: CheckCircle2 },
  pending: { label: "Sending", cls: "bg-amber-50 text-amber-700 border-amber-200", Icon: Clock },
  failed: { label: "Failed to send", cls: "bg-red-50 text-red-700 border-red-200", Icon: AlertCircle },
  skipped: { label: "Not sent", cls: "bg-gray-50 text-gray-600 border-gray-200", Icon: Clock },
  // Distinct from `synced`'s green "Sent to Monday" pill on purpose -- this sits
  // right next to the Board status column showing the raw "Agents to Review"
  // label, and reusing the same green/CheckCircle2 look as a plain send-succeeded
  // state read as a contradiction (looks resolved and still-in-review at once).
  ready_to_test: { label: "Issue Resolved, Ready to Test", cls: "bg-blue-50 text-blue-700 border-blue-200", Icon: CheckCheck },
};

// Once triage has moved the Monday item to one of these board statuses, the
// fix is up for review -- surface that in the CRM's own Status column instead
// of the generic "Sent to Monday", which stops meaning anything useful once
// the item has moved on.
const READY_TO_TEST_BOARD_STATUSES = ["agents to review", "for review"];
const isReadyToTest = (r) =>
  r.sync_status === "synced" &&
  READY_TO_TEST_BOARD_STATUSES.includes(
    (r.monday_status_label || "").trim().toLowerCase()
  );

const StatusChip = ({ status, label }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.skipped;
  const Icon = s.Icon;
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border ${s.cls}`}
    >
      <Icon size={12} />
      {label || s.label}
    </span>
  );
};

const ReportIssuePage = () => {
  const user = useSelector((state) => state.auth?.user);
  const isPrivileged = hasAdminAccess(user) || user?.role === "pa";
  const canDelete = (user?.email || "").toLowerCase() === DELETER_EMAIL;

  const [reports, setReports] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [retrying, setRetrying] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async (targetPage) => {
    setLoading(true);
    try {
      const data = await getErrorReports({
        limit: PAGE_SIZE,
        offset: (targetPage - 1) * PAGE_SIZE,
      });
      // Deleting the last row on a page (or the count shrinking under a page
      // the user is already sitting on) would otherwise strand them on a
      // page with nothing to show -- step back one instead.
      if (!data?.results?.length && targetPage > 1) {
        setPage(targetPage - 1);
        return;
      }
      setReports(data?.results || []);
      setTotalCount(data?.count || 0);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Could not load your reports."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(page);
  }, [page, load]);

  // Back to page 1 after filing a new report, so it's visible immediately --
  // it always sorts first (newest first). If already on page 1, setPage is a
  // no-op (same value), so re-fetch directly instead of relying on the effect.
  const refreshAfterCreate = useCallback(() => {
    if (page === 1) load(1);
    else setPage(1);
  }, [page, load]);

  const isOwnReport = (r) =>
    !!user?.email &&
    (r.reporter_email || "").toLowerCase() === user.email.toLowerCase();

  const retry = async (id) => {
    setRetrying(id);
    try {
      const updated = await retryErrorReport(id);
      setReports((prev) => prev.map((r) => (r.id === id ? updated : r)));
      toast.success("Queued to send again.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not re-send.");
    } finally {
      setRetrying(null);
    }
  };

  const remove = async (report) => {
    if (!window.confirm(`Delete "${report.title}" (${report.reference_number})?`)) {
      return;
    }
    setDeleting(report.id);
    try {
      await deleteErrorReport(report.id);
      toast.success("Deleted.");
      // Re-fetch rather than filter the local list -- deleting the last row on
      // a page needs `count` and the page bounds to stay correct too.
      load(page);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete.");
    } finally {
      setDeleting(null);
    }
  };

  const handleEdited = (updated) => {
    setReports((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
  };

  return (
    <div className="p-4 sm:p-6">
      <PageHeader
        title="Report an Issue"
        subtitle={
          isPrivileged
            ? "Issues reported by staff, sent through to the Monday board"
            : "Issues you have reported, sent through to the Monday board"
        }
        action={
          <button
            onClick={() => setModalOpen(true)}
            className="w-full sm:w-auto px-4 py-2 text-sm rounded-lg text-white flex items-center justify-center gap-2"
            style={{ backgroundColor: BRAND }}
          >
            <Plus size={16} />
            Report an issue
          </button>
        }
      />

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500 py-10 justify-center">
          <Loader2 size={16} className="animate-spin" />
          Loading reports...
        </div>
      ) : reports.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
          <AlertCircle size={28} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-600 mb-1">No issues reported yet.</p>
          <p className="text-sm text-gray-400 mb-5">
            Found something broken? Report it and it goes straight to the
            development board.
          </p>
          <button
            onClick={() => setModalOpen(true)}
            className="px-4 py-2 text-sm rounded-lg text-white inline-flex items-center gap-2"
            style={{ backgroundColor: BRAND }}
          >
            <Plus size={16} />
            Report an issue
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                <tr>
                  <th className="text-left font-medium px-4 py-3">Reference</th>
                  <th className="text-left font-medium px-4 py-3">Issue</th>
                  <th className="text-left font-medium px-4 py-3">Area</th>
                  <th className="text-left font-medium px-4 py-3">Priority</th>
                  <th className="text-left font-medium px-4 py-3">Assigned to</th>
                  {isPrivileged && (
                    <th className="text-left font-medium px-4 py-3">Reported by</th>
                  )}
                  <th className="text-left font-medium px-4 py-3">Board status</th>
                  <th className="text-left font-medium px-4 py-3">Status</th>
                  <th className="text-left font-medium px-4 py-3">Monday</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {reports.map((r) => (
                  <React.Fragment key={r.id}>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-gray-500 text-xs">
                      {r.reference_number}
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      <div className="text-gray-800 break-words">{r.title}</div>
                      <div className="text-xs text-gray-400">
                        {new Date(r.created_at).toLocaleString("en-AU")}
                        {r.attachments?.length
                          ? ` · ${r.attachments.length} file(s)`
                          : ""}
                      </div>
                      {r.updates?.length > 0 && (
                        <button
                          type="button"
                          onClick={() =>
                            setExpandedId(expandedId === r.id ? null : r.id)
                          }
                          className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-[#3a99a3] hover:underline"
                        >
                          <MessageSquare size={12} />
                          {r.updates.length} developer{" "}
                          {r.updates.length === 1 ? "update" : "updates"}
                          {expandedId === r.id ? (
                            <ChevronUp size={12} />
                          ) : (
                            <ChevronDown size={12} />
                          )}
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                      {r.area || "-"}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                      {r.priority || "-"}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                      {r.assignee_name || (
                        <span className="text-gray-400">Unassigned</span>
                      )}
                    </td>
                    {isPrivileged && (
                      <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                        {r.reporter_name || "-"}
                      </td>
                    )}
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                      {r.monday_status_label || (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <StatusChip
                        status={isReadyToTest(r) ? "ready_to_test" : r.sync_status}
                        label={isReadyToTest(r) ? null : r.sync_status_display}
                      />
                      {r.sync_status === "failed" && r.sync_error && (
                        <div
                          className="text-xs text-red-500 mt-1 max-w-xs truncate"
                          title={r.sync_error}
                        >
                          {r.sync_error}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {r.monday_item_url ? (
                        <a
                          href={r.monday_item_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs hover:underline"
                          style={{ color: BRAND }}
                        >
                          Open <ExternalLink size={12} />
                        </a>
                      ) : isPrivileged && r.sync_status !== "synced" ? (
                        <button
                          onClick={() => retry(r.id)}
                          disabled={retrying === r.id}
                          className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900 disabled:opacity-50"
                        >
                          {retrying === r.id ? (
                            <Loader2 size={12} className="animate-spin" />
                          ) : (
                            <RefreshCw size={12} />
                          )}
                          Re-send
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {isOwnReport(r) && (
                          <button
                            onClick={() => setEditingReport(r)}
                            aria-label={`Edit ${r.reference_number}`}
                            className="text-gray-400 hover:text-[#55C5D0]"
                          >
                            <Edit size={14} />
                          </button>
                        )}
                        {(canDelete || isOwnReport(r)) && (
                          <button
                            onClick={() => remove(r)}
                            disabled={deleting === r.id}
                            aria-label={`Delete ${r.reference_number}`}
                            className="text-gray-400 hover:text-red-600 disabled:opacity-50"
                          >
                            {deleting === r.id ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : (
                              <Trash2 size={14} />
                            )}
                          </button>
                        )}
                        {!isOwnReport(r) && !canDelete && (
                          <span className="text-xs text-gray-300">-</span>
                        )}
                      </div>
                    </td>
                  </tr>
                  {expandedId === r.id && (
                    <tr className="bg-gray-50/60">
                      <td colSpan={isPrivileged ? 10 : 9} className="px-4 pb-4 pt-1">
                        <div className="space-y-2">
                          {(r.updates || []).map((u) => (
                            <div
                              key={u.id}
                              className="rounded-lg border border-gray-200 bg-white p-3"
                            >
                              <div className="flex items-center justify-between gap-2 mb-1">
                                <span className="text-xs font-bold text-gray-700">
                                  {u.author_name || "Developer"}
                                </span>
                                {u.posted_at && (
                                  <span className="text-[11px] text-gray-400">
                                    {formatMelbourneDateTime(u.posted_at)}
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">
                                {u.body}
                              </p>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {totalCount > PAGE_SIZE && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-sm text-gray-600">
              <span>
                Showing {(page - 1) * PAGE_SIZE + 1}-
                {Math.min(page * PAGE_SIZE, totalCount)} of {totalCount}
              </span>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1 || loading}
                  className="px-3 py-1.5 rounded-lg border border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Previous
                </button>
                <span className="text-xs text-gray-500">
                  Page {page} of {Math.max(1, Math.ceil(totalCount / PAGE_SIZE))}
                </span>
                <button
                  onClick={() => setPage((p) => (p * PAGE_SIZE < totalCount ? p + 1 : p))}
                  disabled={page * PAGE_SIZE >= totalCount || loading}
                  className="px-3 py-1.5 rounded-lg border border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <ReportIssueModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmitted={refreshAfterCreate}
      />

      <EditIssueModal
        report={editingReport}
        onClose={() => setEditingReport(null)}
        onSaved={handleEdited}
      />
    </div>
  );
};

export default ReportIssuePage;
